import React from 'react'
import styled from '@emotion/styled'
import { v4 as uuidv4 } from 'uuid'
import { isNil } from 'lodash-es'
import { InputContainer } from '../layout'
import { TextButton, TextButtonType, MultiButton, LinkButton } from '../buttons'
import { TextInput } from './TextInput'
import { InputProps } from './BaseInput'
import { OptionTemplateProps, Listbox, ListboxOptions, OptionKey } from '../options/Listbox'
import { Severity, ValidationType } from 'api'
import { notEmpty, NEW_ITEM_PREFIX } from 'utils'
import { iconSet } from '../icons'
import * as constants from 'view/styles/constants'
import { Locale, Translation } from 'locale'

export interface KeyValuePair<K> {
  key: K
  value: string
}

export interface MultiTextInputProps<T, K extends OptionKey> extends InputProps, Pick<ListboxOptions<T, K>, 'getOption' | 'getOptionText'> {
  id?: string
  className?: string
  placeholder?: string
  value: KeyValuePair<K>[]
  maxLength?: number
  listColorPalette?: string[]
  readOnly?: boolean
  readOnlyOptions?: boolean
  disabled?: boolean
  canClear?: boolean
  clearButtonLabel?: string
  onUpdate: (value: KeyValuePair<K>[], ...rest: any[]) => void
}

interface MultiTextInputState {
  currentValue: string | null | undefined
}

const MultiTextInputWrapper = styled.div``

const AddButton = styled(TextButton)`
  min-width: 50px;
`

const SelectedListbox = styled(Listbox)`
  margin-top: 4px;
  border: 1px solid ${constants.CORE_PRIMARY_2};
  border-radius: 6px;
  li {
    height: 34px;
  }
`

const SelectedOption = styled.div<OptionTemplateProps<string, string>>`
  width: 100%;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e1e3e7;
  justify-content: space-between;
  padding: 0 6px;
  border-left: 6px solid ${props => props.optionColor || 'gray'};

  li:only-child > & {
    border-top: none;
    border-bottom: none;
  }
  li:first-of-type > & {
    border-top: none;
    border-top-left-radius: 6px;
  }
  li:last-child > & {
    border-bottom: none;
    border-bottom-left-radius: 6px;
  }
`

const RemoveItemButton = styled(MultiButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  svg {
    width: 8px;
    height: 8px;
  }
  &:hover {
    svg {
      color: red;
    }
  }
`

const ClearButton = styled(LinkButton)`
  width: 100%;
  margin-top: 4px;
  text-align: right;
`

export class MultiTextInput extends React.Component<MultiTextInputProps<any, OptionKey>, MultiTextInputState> {
  state: MultiTextInputState = {
    currentValue: undefined
  }

  private handleUpdate = (currentValue: string) => {
    this.setState({ currentValue })
  }

  private handleAdd = () => {
    if (!isNil(this.state.currentValue)) {
      const newValue = this.props.value.concat({ key: `${NEW_ITEM_PREFIX}-${uuidv4()}`, value: this.state.currentValue })
      this.setState({ currentValue: undefined }, () => this.props.onUpdate(newValue))
    }
  }

  private handleEdit = (key: string, value: string) => {
    const editItemIdx = this.props.value.findIndex(item => item.key === key)
    if (editItemIdx !== -1) {
      const newValue: KeyValuePair<OptionKey>[] = [
        ...this.props.value.slice(0, editItemIdx),
        { key, value },
        ...this.props.value.slice(editItemIdx + 1)
      ]
      this.props.onUpdate(newValue)
    }
  }

  private handleDelete = (optionKey: OptionKey) => {
    const newValue = this.props.value.filter(item => item.key !== optionKey)
    this.props.onUpdate(newValue)
  }

  private handleClear = () => {
    this.props.onUpdate([])
  }

  private renderSelectedOption = (optionProps: OptionTemplateProps<string, string>) => {
    const { disabled, readOnly, readOnlyOptions } = this.props
    const showRemoveButton = !disabled && !readOnly
    const validationIssues = !notEmpty(optionProps.optionText)
      ? [
          {
            errorMessage: Locale.translate(Translation.SC_MISSING_MANDATORY),
            fieldName: '',
            severity: Severity.ERROR,
            type: ValidationType.MISSING_MANDATORY
          }
        ]
      : []

    return (
      <SelectedOption {...optionProps}>
        <TextInput
          value={optionProps.optionText}
          validationIssues={validationIssues}
          disabled={disabled}
          readOnly={readOnly || readOnlyOptions}
          onUpdate={(value: string) => this.handleEdit(optionProps.optionKey, value)}
        />
        {showRemoveButton && (
          <RemoveItemButton onClick={() => this.handleDelete(optionProps.optionKey)}>{iconSet.crossIcon}</RemoveItemButton>
        )}
      </SelectedOption>
    )
  }

  render() {
    const { className, value, listColorPalette, readOnly, disabled, canClear, clearButtonLabel, ...rest } = this.props
    const { currentValue } = this.state

    return (
      <MultiTextInputWrapper className={className}>
        {!this.props.readOnly && (
          <InputContainer>
            <TextInput {...rest} value={currentValue} disabled={disabled} onUpdate={this.handleUpdate} onEnterKey={this.handleAdd} />
            <AddButton type={TextButtonType.Tertiary} disabled={disabled || !notEmpty(currentValue)} onClick={this.handleAdd}>
              {Locale.translate(Translation.TC_BUTTON_ADD)}
            </AddButton>
          </InputContainer>
        )}

        {value.length > 0 && (
          <>
            <SelectedListbox
              optionKeys={value.map(item => item.key)}
              getOption={this.props.getOption}
              getOptionText={this.props.getOptionText}
              optionTemplate={this.renderSelectedOption}
              optionColorPalette={listColorPalette}
            />
            {canClear && (
              <ClearButton disabled={value.length === 0 || readOnly || disabled} onClick={this.handleClear}>
                {clearButtonLabel || Locale.translate(Translation.SC_BUTTON_CLEAR_ALL)}
              </ClearButton>
            )}
          </>
        )}
      </MultiTextInputWrapper>
    )
  }
}
